/*
 * Dashboard — "Sanctuary, not spreadsheet."
 *
 * Redesigned for warmth + reverence while remaining fully unisex. Structure:
 *
 *   1. Atmospheric hero (starlight + ember glow) — time-of-day greeting,
 *      rotating blessing, dual CTA.
 *   2. Ambient captures tray (Unprocessed Downloads) — only shown if there
 *      are pending items.
 *   3. Trial banner — compact, only if applicable.
 *   4. Latest Whispers — reimagined "Recent Downloads" row.
 *   5. Rhythms & Reflection — the former Insight Map (themes cloud +
 *      category ribbon).
 *   6. For today — full-width blessing card with rotating Scripture.
 *
 * All existing functionality is preserved (API contract, click-through to
 * /history, category colours, etc.). Only the composition changes.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Plus,
  Compass,
  Loader2,
  Map,
  Moon,
  Sunrise,
  Sun,
  Sunset,
  ArrowDown,
  Feather,
} from 'lucide-react';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getCategoryHex, getCategoryBadgeStyle } from '../utils/categoryColors';
import TrialBanner from '../components/TrialBanner';
import RotatingScripture from '../components/RotatingScripture';
import UnprocessedDownloadsTray from '../components/UnprocessedDownloadsTray';
import InAppVoiceCapture from '../components/InAppVoiceCapture';
import FirstRunWelcome from '../components/FirstRunWelcome';
import Starlight from '../components/Starlight';
import SelahFamily from '../components/SelahFamily';

// Palette — sourced from the "Restful Night-Mode" tokens
const PAL = {
  gold: '#D4AF37',
  sand: '#D2B48C',
  alabaster: '#FDFBF7',
  hairline: 'rgba(253, 251, 247, 0.08)',
  emberSoft: 'rgba(212, 175, 55, 0.10)',
  emberSofter: 'rgba(212, 175, 55, 0.04)',
};

// ── Time-of-day greeting ────────────────────────────────────────────
function useTimeOfDay() {
  return useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) {
      return {
        band: 'late-night',
        label: 'Peace tonight',
        blessing: 'The Lord neither slumbers nor sleeps — He watches with you.',
        Icon: Moon,
      };
    }
    if (h < 12) {
      return {
        band: 'morning',
        label: 'Good morning',
        blessing: 'His mercies are new this morning — great is His faithfulness.',
        Icon: Sunrise,
      };
    }
    if (h < 17) {
      return {
        band: 'afternoon',
        label: 'Good afternoon',
        blessing: 'Selah — pause, breathe, and see what He is doing.',
        Icon: Sun,
      };
    }
    if (h < 21) {
      return {
        band: 'evening',
        label: 'Good evening',
        blessing: 'Rest is holy. Let today\u2019s downloads settle in your heart.',
        Icon: Sunset,
      };
    }
    return {
      band: 'night',
      label: 'A restful night',
      blessing: 'Be still and know that He is God.',
      Icon: Moon,
    };
  }, []);
}

// ── Section label with hairline gold ornament ───────────────────────
function SectionLabel({ children, id, ornament = true }) {
  return (
    <div className="flex items-center gap-4 mb-5" id={id}>
      {ornament && (
        <div
          className="flex-1 h-px"
          style={{
            background:
              `linear-gradient(90deg, transparent, var(--sd-atmo-hairline), transparent)`,
          }}
        />
      )}
      <span
        className="text-[10.5px] uppercase tracking-[0.42em]"
        style={{ color: 'var(--sd-atmo-accent)', fontFamily: '"Playfair Display", serif' }}
      >
        {children}
      </span>
      {ornament && (
        <div
          className="flex-1 h-px"
          style={{
            background:
              `linear-gradient(90deg, transparent, var(--sd-atmo-hairline), transparent)`,
          }}
        />
      )}
    </div>
  );
}

// ── First name helper ───────────────────────────────────────────────
function firstNameOf(user) {
  if (!user) return '';
  if (user.name && typeof user.name === 'string') {
    const trimmed = user.name.trim();
    if (trimmed) return trimmed.split(/\s+/)[0];
  }
  if (user.email && typeof user.email === 'string') {
    return user.email.split('@')[0].split('.')[0];
  }
  return '';
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tod = useTimeOfDay();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const data = await api.getDashboard();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={48} className="animate-spin" style={{ color: PAL.gold }} />
      </div>
    );
  }

  const pieData = Object.entries(stats?.categories_breakdown || {})
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: key,
      value,
      color: getCategoryHex(key),
    }));

  const hasInsightData =
    (stats?.top_themes && stats.top_themes.length > 0) || pieData.length > 0;

  const firstName = firstNameOf(user);
  const TodIcon = tod.Icon;

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      {/* ─────────── Atmospheric Hero ─────────── */}
      <section
        className="relative overflow-hidden"
        data-testid="dashboard-hero"
      >
        {/* Ember radial glow (top-right) */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(212,175,55,0.16) 0%, rgba(212,175,55,0.06) 40%, rgba(0,0,0,0) 70%)',
            filter: 'blur(20px)',
          }}
        />
        {/* Sand radial glow (bottom-left) */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(210,180,140,0.10) 0%, rgba(0,0,0,0) 70%)',
            filter: 'blur(30px)',
          }}
        />
        {/* Starlight canvas */}
        <div className="absolute inset-0" aria-hidden>
          <Starlight count={48} />
        </div>
        {/* Subtle grain */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24">
          {/* Time-of-day chip */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6"
            style={{
              background: PAL.emberSofter,
              border: `1px solid ${PAL.hairline}`,
            }}
            data-testid="dashboard-tod-chip"
          >
            <TodIcon size={13} style={{ color: 'var(--sd-atmo-accent-strong)' }} />
            <span
              className="text-[10.5px] uppercase tracking-[0.32em]"
              style={{ color: 'var(--sd-atmo-accent)' }}
            >
              {tod.band.replace('-', ' ')}
            </span>
          </motion.div>

          {/* Big serif greeting */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="text-[36px] sm:text-[52px] lg:text-[62px] leading-[1.05] font-semibold tracking-tight"
            style={{
              color: 'var(--sd-fg-strong)',
              fontFamily: '"Playfair Display", serif',
              letterSpacing: '-0.015em',
            }}
            data-testid="dashboard-greeting"
          >
            {tod.label}
            {firstName ? (
              <>
                ,{' '}
                <span style={{ color: 'var(--sd-atmo-accent-strong)' }}>{firstName}</span>
                .
              </>
            ) : (
              '.'
            )}
          </motion.h1>

          {/* Rotating blessing subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-5 max-w-2xl text-[16px] sm:text-[18px] leading-relaxed italic"
            style={{ color: 'var(--sd-fg-secondary)', fontFamily: '"Crimson Text", serif' }}
            data-testid="dashboard-blessing"
          >
            {tod.blessing}
          </motion.p>

          {/* Dual CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Button
              onClick={() => navigate('/record')}
              data-testid="dashboard-new-download-button"
              className="h-11 px-6 text-[14px] font-semibold rounded-xl"
              style={{
                background: `linear-gradient(180deg, ${PAL.gold} 0%, #B7912D 100%)`,
                color: '#1A1204',
                border: `1px solid ${PAL.gold}`,
                boxShadow:
                  '0 10px 28px -10px rgba(212, 175, 55, 0.55), 0 3px 12px -6px rgba(0,0,0,0.6)',
              }}
            >
              <Feather size={16} className="mr-2" />
              Begin a Download
            </Button>
            <a
              href="#rhythms"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('rhythms')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }}
              data-testid="dashboard-rhythms-anchor"
              className="h-11 px-5 inline-flex items-center gap-2 text-[13.5px] rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)]"
              style={{
                color: 'var(--sd-fg)',
                border: `1px solid var(--sd-atmo-hairline)`,
                background: 'transparent',
              }}
            >
              <ArrowDown size={14} />
              See your rhythms
            </a>
          </motion.div>

          {/* Tiny meta line */}
          {stats?.total_entries != null && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 text-[11.5px] tracking-wide"
              style={{ color: 'var(--sd-fg-faint)' }}
              data-testid="dashboard-hero-meta"
            >
              You&rsquo;ve been listening for{' '}
              <span style={{ color: 'var(--sd-atmo-accent-strong)' }}>{stats.total_entries}</span>{' '}
              {stats.total_entries === 1 ? 'download' : 'downloads'} so far.
            </motion.p>
          )}
        </div>

        {/* Hairline divider at hero base */}
        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${PAL.hairline}, transparent)`,
          }}
        />
      </section>

      {/* ─────────── Body ─────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-14">
        {/* Trial banner (if applicable) */}
        <TrialBanner />

        {/* First-run gentle welcome (once per user, once per device) */}
        <FirstRunWelcome />

        {/* In-app voice capture (no setup) — sits above the tray */}
        <InAppVoiceCapture onCaptured={fetchDashboard} />

        {/* Ambient captures tray */}
        <UnprocessedDownloadsTray onPromoted={fetchDashboard} />

        {/* ─────────── Latest Whispers ─────────── */}
        {stats?.recent_entries && stats.recent_entries.length > 0 && (
          <section data-testid="dashboard-latest-whispers">
            <SectionLabel>· latest whispers ·</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.recent_entries.slice(0, 6).map((entry, idx) => {
                const catHex = entry.analysis?.category
                  ? getCategoryHex(entry.analysis.category)
                  : PAL.gold;
                return (
                  <motion.button
                    key={entry.id}
                    type="button"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: idx * 0.05 }}
                    whileHover={{ y: -2 }}
                    onClick={() => navigate('/history')}
                    className="relative text-left rounded-2xl overflow-hidden group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)]"
                    style={{
                      background:
                        'linear-gradient(180deg, #1C1E21 0%, #17191B 100%)',
                      border: `1px solid ${PAL.hairline}`,
                      boxShadow:
                        '0 6px 18px -14px rgba(0,0,0,0.7), inset 0 1px 0 rgba(253,251,247,0.02)',
                    }}
                    data-testid={`whisper-card-${entry.id}`}
                  >
                    {/* Category accent bar (top) */}
                    <div
                      aria-hidden
                      className="h-[3px] w-full opacity-70"
                      style={{
                        background: `linear-gradient(90deg, ${catHex}, transparent)`,
                      }}
                    />
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-4">
                        {entry.analysis?.category ? (
                          <Badge
                            style={{
                              ...getCategoryBadgeStyle(entry.analysis.category),
                            }}
                            className="border font-semibold text-[10.5px] uppercase tracking-[0.18em]"
                          >
                            {entry.analysis.category}
                          </Badge>
                        ) : (
                          <span
                            className="text-[10.5px] uppercase tracking-[0.24em]"
                            style={{ color: PAL.sand }}
                          >
                            unread
                          </span>
                        )}
                        <span
                          className="text-[10.5px] whitespace-nowrap"
                          style={{ color: 'rgba(253, 251, 247, 0.42)' }}
                        >
                          {format(new Date(entry.created_at), 'MMM d')}
                        </span>
                      </div>

                      {entry.analysis?.themes && entry.analysis.themes.length > 0 ? (
                        <div className="mb-1">
                          <p
                            className="text-[15px] leading-snug"
                            style={{
                              color: PAL.alabaster,
                              fontFamily: '"Playfair Display", serif',
                            }}
                          >
                            {entry.analysis.themes.slice(0, 2).join(' · ')}
                          </p>
                        </div>
                      ) : (
                        <p
                          className="text-[13px] italic"
                          style={{ color: 'rgba(253, 251, 247, 0.56)' }}
                        >
                          A quiet moment saved. Open to unfold.
                        </p>
                      )}

                      {entry.analysis?.themes && entry.analysis.themes.length > 2 && (
                        <p
                          className="text-[11.5px] mt-2"
                          style={{ color: 'rgba(253, 251, 247, 0.42)' }}
                        >
                          +{entry.analysis.themes.length - 2} more themes
                        </p>
                      )}
                    </div>

                    {/* Hover accent */}
                    <div
                      aria-hidden
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
                      style={{
                        background:
                          `radial-gradient(circle at 50% 100%, ${catHex}18 0%, transparent 60%)`,
                      }}
                    />
                  </motion.button>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/history')}
                className="text-[12px] tracking-wide underline-offset-4 hover:underline"
                style={{ color: 'var(--sd-muted-fg)' }}
                data-testid="dashboard-see-all-history"
              >
                See all downloads →
              </button>
            </div>
          </section>
        )}

        {/* ─────────── Rhythms & Reflection ─────────── */}
        <section id="rhythms" data-testid="dashboard-rhythms">
          <SectionLabel>· rhythms &amp; reflection ·</SectionLabel>
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: 'var(--sd-atmo-hairline)',
                border: `1px solid var(--sd-atmo-hairline)`,
              }}
            >
              <Map size={17} style={{ color: 'var(--sd-atmo-accent-strong)' }} />
            </div>
            <div>
              <h2
                className="text-[22px] leading-tight font-semibold"
                style={{
                  color: 'var(--sd-fg-strong)',
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                A quiet map of what keeps rising up
              </h2>
              <p className="text-[13px] leading-relaxed mt-0.5"
                 style={{ color: 'var(--sd-muted-fg)' }}>
                Not a scoreboard — a reflection of themes and categories emerging from your walk.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Recurring Themes cloud */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="lg:col-span-7"
            >
              <Card
                className="p-6 rounded-2xl h-full"
                style={{
                  background: '#1C1E21',
                  border: `1px solid ${PAL.hairline}`,
                  boxShadow: '0 8px 30px -18px rgba(0,0,0,0.7)',
                }}
              >
                <div className="mb-4 flex items-center gap-2">
                  <Compass size={16} style={{ color: PAL.gold }} />
                  <h3
                    className="text-[15px] font-semibold"
                    style={{ color: PAL.alabaster }}
                  >
                    Recurring Themes
                  </h3>
                </div>
                <p
                  className="text-[12px] leading-relaxed mb-5"
                  style={{ color: 'rgba(253, 251, 247, 0.52)' }}
                >
                  Larger words rise more often in your downloads.
                </p>

                {stats?.top_themes && stats.top_themes.length > 0 ? (
                  (() => {
                    const maxCount = Math.max(...stats.top_themes.map((t) => t.count));
                    const minCount = Math.min(...stats.top_themes.map((t) => t.count));
                    const range = Math.max(1, maxCount - minCount);
                    const sized = stats.top_themes.map((t) => {
                      const weight = (t.count - minCount) / range; // 0..1
                      const fontSize = 15 + Math.round(weight * 15);
                      const opacity = 0.55 + weight * 0.45;
                      const color = weight > 0.7 ? PAL.sand : PAL.alabaster;
                      return { ...t, fontSize, opacity, color };
                    });
                    return (
                      <div
                        className="flex flex-wrap gap-x-5 gap-y-3 items-baseline pt-2"
                        data-testid="dashboard-theme-cloud"
                      >
                        {sized.map((t, idx) => (
                          <span
                            key={idx}
                            className="leading-tight transition-opacity"
                            style={{
                              fontSize: `${t.fontSize}px`,
                              opacity: t.opacity,
                              color: t.color,
                              fontFamily: '"Playfair Display", serif',
                              letterSpacing: '0.005em',
                            }}
                            title={`${t.theme} \u2014 ${t.count} time${t.count === 1 ? '' : 's'}`}
                          >
                            {t.theme}
                          </span>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <div className="py-10 text-center">
                    <p
                      className="text-[13.5px] italic max-w-xs mx-auto leading-relaxed"
                      style={{ color: 'rgba(253, 251, 247, 0.42)', fontFamily: '"Crimson Text", serif' }}
                    >
                      No themes to map yet. Record your first download and the words that keep rising will gather here.
                    </p>
                  </div>
                )}
              </Card>
            </motion.div>

            {/* Category Mapping */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <Card
                className="p-6 rounded-2xl h-full"
                style={{
                  background: '#1C1E21',
                  border: `1px solid ${PAL.hairline}`,
                  boxShadow: '0 8px 30px -18px rgba(0,0,0,0.7)',
                }}
              >
                <h3
                  className="text-[15px] font-semibold mb-1"
                  style={{ color: PAL.alabaster }}
                >
                  Category Mapping
                </h3>
                <p
                  className="text-[12px] mb-5 leading-relaxed"
                  style={{ color: 'rgba(253, 251, 247, 0.52)' }}
                >
                  Where your downloads are quietly landing.
                </p>

                {pieData.length > 0 ? (
                  (() => {
                    const total = pieData.reduce((s, e) => s + e.value, 0) || 1;
                    return (
                      <>
                        <div
                          className="w-full h-2.5 rounded-full overflow-hidden flex"
                          style={{
                            background: 'rgba(253, 251, 247, 0.04)',
                            border: `1px solid ${PAL.hairline}`,
                          }}
                          data-testid="dashboard-category-ribbon"
                          aria-label="Category proportion ribbon"
                        >
                          {pieData.map((entry, idx) => (
                            <div
                              key={idx}
                              className="h-full"
                              style={{
                                width: `${(entry.value / total) * 100}%`,
                                backgroundColor: entry.color,
                                opacity: 0.62,
                              }}
                              title={`${entry.name} \u2014 ${entry.value}`}
                            />
                          ))}
                        </div>
                        <div className="space-y-3 mt-5">
                          {pieData.map((entry, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between text-[13px]"
                            >
                              <div className="flex items-center gap-3">
                                <span
                                  className="w-1.5 h-1.5 rounded-full inline-block"
                                  style={{
                                    backgroundColor: entry.color,
                                    opacity: 0.85,
                                  }}
                                />
                                <span style={{ color: PAL.alabaster }}>
                                  {entry.name}
                                </span>
                              </div>
                              <span
                                className="text-[11.5px] tabular-nums"
                                style={{ color: 'rgba(253, 251, 247, 0.52)' }}
                              >
                                {entry.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <div className="py-10 text-center">
                    <p
                      className="text-[13.5px] italic max-w-xs mx-auto leading-relaxed"
                      style={{
                        color: 'rgba(253, 251, 247, 0.42)',
                        fontFamily: '"Crimson Text", serif',
                      }}
                    >
                      Nothing to map yet. Start recording and the shape of your walk will gently appear.
                    </p>
                  </div>
                )}
              </Card>
            </motion.div>
          </div>

          {!hasInsightData && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-center text-[11.5px] mt-8 italic max-w-md mx-auto leading-relaxed"
              style={{
                color: 'var(--sd-fg-faint)',
                fontFamily: '"Crimson Text", serif',
              }}
            >
              Your Insight Map fills in as you record. Nothing to compete against — just a reflective view of what keeps rising up.
            </motion.p>
          )}
        </section>

        {/* ─────────── Blessing card ─────────── */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="dashboard-blessing-card"
        >
          <SectionLabel>· for today ·</SectionLabel>
          <Card
            className="force-night relative overflow-hidden rounded-2xl p-8 sm:p-10 text-center"
            style={{
              background:
                'linear-gradient(180deg, #1C1E21 0%, #17191B 100%)',
              border: `1px solid ${PAL.hairline}`,
              boxShadow: '0 12px 40px -22px rgba(0,0,0,0.7)',
            }}
          >
            {/* Subtle grain */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.035]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />
            {/* Ember glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(0,0,0,0) 65%)',
                filter: 'blur(20px)',
              }}
            />
            <div className="relative max-w-lg mx-auto">
              <RotatingScripture
                scriptureKey="dashboard-encouragement"
                mood="guidance"
                variant="inline"
                center={true}
              />
            </div>
          </Card>
        </motion.section>

        {/* ─────────── The Selah Family — Coming Soon ─────────── */}
        <SelahFamily />
      </div>
    </div>
  );
};

export default Dashboard;
